import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography
} from '@mui/material';
import { IconArrowLeft, IconPlus, IconX } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';

const WEEKDAYS = [
    { value: 0, label: 'Segunda-feira' },
    { value: 1, label: 'Terça-feira' },
    { value: 2, label: 'Quarta-feira' },
    { value: 3, label: 'Quinta-feira' },
    { value: 4, label: 'Sexta-feira' },
    { value: 5, label: 'Sábado' },
    { value: 6, label: 'Domingo' }
];

const SLOT_DURATION_OPTIONS = [15, 20, 30, 45, 60, 90, 120];

const AttendanceHours = () => {
    const history = useHistory();
    const [appId, setAppId] = useState(null);
    const [horarios, setHorarios] = useState([]);
    const [slotMinutes, setSlotMinutes] = useState(60);
    const [loading, setLoading] = useState(true);
    const [savingSlot, setSavingSlot] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [addDialogWeekday, setAddDialogWeekday] = useState(null);
    const [novoInicio, setNovoInicio] = useState('09:00');
    const [novoFim, setNovoFim] = useState('18:00');
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState('');

    useEffect(() => {
        api.get('v1/apps/default/').then((res) => setAppId(res.data.app?.id));
    }, []);

    const carregar = async () => {
        if (!appId) return;
        setLoading(true);
        try {
            const res = await api.get(`v1/apps/${appId}/attendance_hours/`);
            setHorarios(res.data.horarios || []);
            setSlotMinutes(res.data.slot_minutes || 60);
        } catch (error) {
            console.error('Erro ao carregar horário de atendimento', error);
            setStatus({ type: 'error', message: 'Erro ao carregar horário de atendimento.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const handleSalvarSlotMinutes = async (novoValor) => {
        setSlotMinutes(novoValor);
        setSavingSlot(true);
        try {
            await api.patch(`v1/apps/${appId}/`, { availability_slot_minutes: novoValor });
            setStatus({ type: 'success', message: 'Duração do horário atualizada.' });
        } catch (error) {
            console.error('Erro ao salvar duração do horário', error);
            setStatus({ type: 'error', message: 'Erro ao salvar duração do horário.' });
        } finally {
            setSavingSlot(false);
        }
    };

    const openAddDialog = (weekday) => {
        setAddDialogWeekday(weekday);
        setNovoInicio('09:00');
        setNovoFim('18:00');
        setDialogError('');
    };

    const handleAdicionarFaixa = async () => {
        if (novoInicio >= novoFim) {
            setDialogError('O horário de início precisa ser antes do de término.');
            return;
        }
        setSaving(true);
        setDialogError('');
        try {
            await api.post(`v1/apps/${appId}/attendance_hours/`, {
                weekday: addDialogWeekday,
                start_time: novoInicio,
                end_time: novoFim
            });
            setAddDialogWeekday(null);
            carregar();
        } catch (error) {
            console.error('Erro ao adicionar faixa de horário', error);
            setDialogError(error.response?.data?.error || 'Erro ao adicionar faixa de horário.');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoverFaixa = async (horarioId) => {
        try {
            await api.delete(`v1/apps/${appId}/attendance_hours/${horarioId}/`);
            setHorarios((prev) => prev.filter((h) => h.id !== horarioId));
        } catch (error) {
            console.error('Erro ao remover faixa de horário', error);
            setStatus({ type: 'error', message: 'Erro ao remover faixa de horário.' });
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton onClick={() => history.goBack()}>
                    <IconArrowLeft />
                </IconButton>
                <Typography variant="h4">Horário de Atendimento</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Define os dias e faixas de horário em que a agenda considera "disponível" - usado pela API de disponibilidade de
                agenda, que também leva em conta os compromissos já marcados (inclusive os sincronizados com o Google Agenda).
            </Typography>

            {status.message && (
                <Alert severity={status.type || 'info'} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Duração de cada horário
                    </Typography>
                    <TextField
                        select
                        size="small"
                        value={slotMinutes}
                        onChange={(e) => handleSalvarSlotMinutes(Number(e.target.value))}
                        disabled={savingSlot || !appId}
                        sx={{ minWidth: 200 }}
                    >
                        {SLOT_DURATION_OPTIONS.map((min) => (
                            <MenuItem key={min} value={min}>
                                {min} minutos
                            </MenuItem>
                        ))}
                    </TextField>
                </CardContent>
            </Card>

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {WEEKDAYS.map((dia) => {
                        const faixasDoDia = horarios.filter((h) => h.weekday === dia.value);
                        return (
                            <Grid item xs={12} sm={6} key={dia.value}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                {dia.label}
                                            </Typography>
                                            <IconButton size="small" onClick={() => openAddDialog(dia.value)} title="Adicionar faixa de horário">
                                                <IconPlus size={18} />
                                            </IconButton>
                                        </Box>

                                        {faixasDoDia.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Sem atendimento
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {faixasDoDia.map((h) => (
                                                    <Chip
                                                        key={h.id}
                                                        label={`${h.start_time} - ${h.end_time}`}
                                                        onDelete={() => handleRemoverFaixa(h.id)}
                                                        deleteIcon={<IconX size={14} />}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Dialog open={addDialogWeekday !== null} onClose={() => setAddDialogWeekday(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Adicionar faixa de horário</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        {dialogError && <Alert severity="error">{dialogError}</Alert>}
                        <TextField
                            label="Início"
                            type="time"
                            value={novoInicio}
                            onChange={(e) => setNovoInicio(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Término"
                            type="time"
                            value={novoFim}
                            onChange={(e) => setNovoFim(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogWeekday(null)} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button onClick={handleAdicionarFaixa} variant="contained" disabled={saving}>
                        Adicionar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AttendanceHours;
